import React, { useEffect, useState } from 'react'

import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

interface UserPresence {
  user_id: string
  online_at: string
  user_info: {
    full_name: string
    role: string
  }
}

export const useUserPresence = () => {
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set())
  const { profile } = useAuth()

  useEffect(() => {
    if (!profile?.id) return

    const channel = supabase.channel('user_presence')

    // Configurar presença
    const userStatus = {
      user_id: profile.id,
      online_at: new Date().toISOString(),
      user_info: {
        full_name: profile.full_name || '',
        role: profile.role
      }
    }

    // Escutar mudanças de presença
    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState()
        const currentOnlineUsers = new Set<string>()
        
        Object.keys(state).forEach(key => {
          const presences = state[key] as any[]
          presences.forEach(presence => {
            if (presence.user_id) {
              currentOnlineUsers.add(presence.user_id)
            }
          })
        })
        
        setOnlineUsers(currentOnlineUsers)
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        setOnlineUsers(prev => {
          const updated = new Set(prev)
          newPresences.forEach((presence: any) => {
            if (presence.user_id) {
              updated.add(presence.user_id)
            }
          })
          return updated
        })
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        setOnlineUsers(prev => {
          const updated = new Set(prev)
          leftPresences.forEach((presence: any) => {
            if (presence.user_id) {
              updated.delete(presence.user_id)
            }
          })
          return updated
        })
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          // Registrar presença do usuário atual
          await channel.track(userStatus)
        }
      })

    // Cleanup
    return () => {
      channel.untrack()
      supabase.removeChannel(channel)
    }
  }, [profile?.id, profile?.full_name, profile?.role])

  const isUserOnline = (userId: string) => {
    return onlineUsers.has(userId)
  }

  return {
    onlineUsers: Array.from(onlineUsers),
    isUserOnline
  }
}